import type { ConsentBasis } from "@/shared/api/types";

/**
 * Every consent word the portal shows, in ONE module (M6 §7, ADR-0009 §2). The text is copied from the
 * canonical draft, `nex-access-control-docs/privacy/consent-wording.md`, which is **not yet reviewed by
 * counsel** and the version below travels with the sign-up acceptance as `termsVersion`.
 *
 * What is copied and what is not: the reader-facing sentences are verbatim. The drafting notes in that file
 * ("verify with counsel", "Flag for counsel", the source path under the retention table) are editorial and
 * stay in the docs repo. Since 2026-10-16 (Kumar) no bracketed placeholder is left in either public document
 * (grievance officer, contact, liability and governing law are filled in); `wording.test.ts` and
 * `LegalPages.test.tsx` fail if a `[Placeholder` / `placeholder]` marker ever comes back.
 *
 * Editing rule (from the source file): change one word here and the version string is bumped there and here in
 * the same change, so an organisation's stored `termsVersion` always names the words it actually accepted.
 */

/** Sent as `termsVersion` when an organisation is created; shown beside the acceptance date in Settings. */
export const CONSENT_WORDING_VERSION = "2026-10-16";

/**
 * **M20: the wording id a student's consents are recorded under** (`student_consents.wording_version`, at most
 * 20 characters): the "Students" section of the source file, `students-v1`. It is its own id because the
 * guardian-authorisation and biometric paragraphs are a separate draft for a separate review (children's data
 * under the DPDP Act), bumped on its own when counsel changes a word there — the organisation-level version
 * above says nothing about them. Recorded by Register student, the Record-a-consent dialog and the import.
 */
export const STUDENT_CONSENT_WORDING_VERSION = "students-v1";

/**
 * **M24 (contracts §3, plan §9): the wording id an adult's face-recognition consent is recorded under**
 * (`biometric_consents.wording_version`, and the value of the photo routes' `?faceConsent=` parameter). Its own
 * id, like the students one, because it is a separate paragraph for a separate review; bumped here and in
 * `@nacs/shared` `FACE_CONSENT_WORDING_VERSION` in the same change when counsel changes a word below.
 */
export const FACE_CONSENT_WORDING_VERSION = "face-v1";

/**
 * The sentence a member confirms when consent is recorded for people who already have a photo — Face Kiosk →
 * Consent's bulk confirmation and the person card's "Confirm consent for this photo".
 */
export const FACE_CONSENT_ATTESTATION =
  "I confirm this person has consented to face recognition for attendance.";

/**
 * **(K 2026-10-07) The tick beside every photo picker**, portal and app (C2-12): without it an adult's photo is
 * not saved. Phrased as a fact about the person, because the member ticking it is recording what the person
 * agreed to, not agreeing to anything themselves.
 */
export const FACE_CONSENT_TICK = "This person has agreed to face recognition for attendance.";

/**
 * The notice behind the tick's "What this means" — what an adult is agreeing to. **Draft:** lane D finalises it
 * in `privacy/consent-wording.md` for counsel, and a change here bumps {@link FACE_CONSENT_WORDING_VERSION}.
 * Short enough for a popover; the title is a UI heading, not reviewed wording.
 */
export const FACE_CONSENT_NOTICE = {
  title: "Face recognition notice",
  summary:
    "The saved photo is used to recognise this person's face for attendance, at your terminals and on Face Kiosk phones.",
  paragraphs: [
    "With this person's agreement, their saved photo becomes a face template that lets a terminal or a Face Kiosk phone at your gate recognise them when they arrive and leave. The template is stored encrypted and is used only for attendance and access in your organisation.",
    "Each Face Kiosk scan keeps a small photo of the face and the phone's location for 90 days, so the scan can be checked later. The attendance record itself is kept like any other access event.",
    "They can withdraw their agreement at any time: the photo and its template are then removed, and they keep using a card, a PIN or a manual punch. Tell them this before you tick the box.",
  ],
} as const;

/** Where the words are drafted and reviewed; shown at the foot of each rendered document. */
export const CONSENT_WORDING_SOURCE = "nex-access-control-docs/privacy/consent-wording.md";

/**
 * The notice shown wherever a person is enrolled (Add person, the CSV import confirmation), above the consent
 * basis. Two lines and "Read more", then the full text — the source file keeps it under 120 words so the
 * dialog never needs a scrollbar. The title is a UI heading, not reviewed wording.
 */
export const ENROLMENT_NOTICE = {
  title: "Enrolment notice",
  summary:
    "We collect this person's name, mobile, card number and face photo to give them access, and log their visits.",
  paragraphs: [
    "We collect this person's name, mobile number (optional), card number and a face photo, to give them access. The photo creates a face template on the terminal, deleted when you remove this person. We log access events (time, door, method, snapshot) for as long as your plan allows, and keep the photo until removal.",
    "Your organisation is responsible for this data. Nexborg processes it for you, in India, with DigitalOcean (servers and database, Bangalore), Cloudflare (file storage, portal) and Airtel (SMS).",
    "They can ask you to see, correct, delete their data, or withdraw consent, and raise concerns with you; Nexborg helps you respond quickly. Tell them before you enrol them.",
  ],
  /** The disclosure control's two labels. */
  expandLabel: "Read more",
  collapseLabel: "Show less",
} as const;

/**
 * **M17 (plan §8): the line at the gym counter, when a number is taken for an enquiry.**
 *
 * A lead is a prospect, not a Person (ADR-0017's prospects addendum, 2026-09-22), so the enrolment
 * notice above is the wrong text: nothing here is a face, a card or a door, and the promise is much
 * smaller — somebody will ring back about a price. It is ONE line rather than a teaser with a "Read
 * more", because the whole capture is meant to take twenty seconds and a notice that needs opening
 * is a notice nobody reads out.
 *
 * It is a draft for the same counsel review as everything else in this module, and it does not bump
 * {@link CONSENT_WORDING_VERSION} on its own — the precedent the source file sets for the
 * fingerprint and billing paragraphs. The version is nonetheless recorded WITH the lead
 * (`leads.consent.wordingVersion`), so a change here is always traceable to the words a particular
 * person agreed to. Add the paragraph to `privacy/consent-wording.md` when that file is next edited.
 */
export const LEAD_CAPTURE_NOTICE = {
  line: "We use this number to ring this person back about membership and prices, and nothing else. It is deleted automatically once the enquiry has gone cold, and they can ask you to remove it sooner.",
  /** Shown under the basis field, as `CONSENT_BASIS_HINT.recorded` is on the enrolment form. */
  recorded: "Recorded with your name, the time, and the version of these words.",
  /** Why the field is optional: an enquiry taken over the phone before anybody read the line out. */
  optional: "Leave it blank if nobody read this out — the enquiry is still captured.",
} as const;

/**
 * The four bases a consent record can carry (ADR-0009 §2), each with the one line that explains it.
 * `PersonFields` shows the label in the select and the explanation under it once one is chosen.
 */
export const CONSENT_BASIS_WORDING: Record<ConsentBasis, { label: string; explanation: string }> = {
  employment: {
    label: "Employment",
    explanation: "This person works for your organisation as an employee.",
  },
  visitor: {
    label: "Visitor consent",
    explanation: "This person is a visitor who has agreed to be enrolled for this visit.",
  },
  contract: {
    label: "Contract",
    explanation: "This person works for you under a contract, such as a vendor, contractor or gig worker.",
  },
  // M21 (BR-23; draft in privacy/coworking-consent-wording.md §1 — [counsel] to confirm; same text as the app's
  // `consentWording.ts`): the person's own agreement, e.g. a co-working member or a client company's employee.
  consent: {
    label: "Consent",
    explanation:
      "This person has agreed themselves to be registered here. They ticked the box; you did not tick it for them.",
  },
  other: {
    label: "Other",
    explanation: "None of the above; add a note explaining why you're enrolling them.",
  },
};

/** Shown under the select before a basis is chosen, and appended to the explanation after one is. */
export const CONSENT_BASIS_HINT = {
  unchosen: "Why their face and cards may be processed.",
  recorded: "Recorded with your name and the time.",
} as const;

/**
 * The sign-up acceptance line, split so the two document links sit inside the label:
 * "I agree to the Terms of Service and Privacy Notice on behalf of <organisation>."
 */
export const SIGNUP_ACCEPTANCE = {
  lead: "I agree to the",
  termsLabel: "Terms of Service",
  join: "and",
  privacyLabel: "Privacy Notice",
  tail: "on behalf of",
  /**
   * Stands in for the organisation's name until one has been typed. First person, like the rest of
   * the sentence: "I agree … on behalf of your organisation" put the reader in two voices at once.
   * A portal-only placeholder — the reviewed draft in privacy/consent-wording.md names the
   * organisation outright — so it is not part of what legal signs off.
   */
  organisationFallback: "my organisation",
  /** Why "Create organisation" is held back while the box is unticked. */
  requiredReason: "Accept the terms and the privacy notice to create the organisation.",
} as const;

/** The whole acceptance sentence as one string: the accessible name of the checkbox. */
export function signupAcceptanceText(organisation: string): string {
  const { lead, termsLabel, join, privacyLabel, tail, organisationFallback } = SIGNUP_ACCEPTANCE;
  const name = organisation.trim() || organisationFallback;
  return `${lead} ${termsLabel} ${join} ${privacyLabel} ${tail} ${name}.`;
}

export interface NoticeSection {
  heading: string;
  body: string;
  /** Rendered as a list under the body. */
  bullets?: string[];
  /** One table under the body (what we keep and for how long, in the privacy notice). */
  table?: { caption: string; head: string[]; rows: string[][] };
}

export interface LegalDocument {
  title: string;
  intro: string;
  sections: NoticeSection[];
}

/** Terms of service, rendered at /legal/terms. Counsel review is still recommended (see the source file). */
export const TERMS_DOCUMENT: LegalDocument = {
  title: "Terms of Service",
  intro:
    "The agreement between NexBorg Software Solutions Private Limited and your organisation for the NexBorg Access service: the portal, the API, and the software that talks to your terminals.",
  sections: [
    {
      heading: "1. What this service is",
      body: 'NexBorg Access ("we", "Nexborg") helps your organisation manage who can enter your premises, using face and card terminals. You enrol your people; we run the software that talks to the terminals and stores the records.',
    },
    {
      heading: "2. Your organisation's responsibilities",
      body: "You decide whose data goes into the system and why. Under India's Digital Personal Data Protection Act 2023, that makes your organisation the data fiduciary. You're responsible for telling your employees, contractors and visitors what you collect and why, and for having a lawful basis to enrol them.",
    },
    {
      heading: "3. Nexborg's responsibilities",
      body: "We process the data on your instructions, as your data processor. We don't sell it, use it for our own purposes, or share it beyond what this document and our Privacy Notice describe.",
    },
    {
      heading: "4. Acceptable use",
      body: "Don't enrol anyone you're not entitled to enrol, don't try to break the service's security, and don't resell access to it without our written agreement.",
    },
    {
      heading: "5. Plans, trial and limits",
      body: "Your plan sets limits on devices, people and how long we keep records (see the Privacy Notice). New organisations can start with a free trial; its length is shown when you start it. Going over a limit may pause new enrolments until you upgrade or remove records.",
    },
    {
      heading: "6. Suspension and deletion",
      body: "We may suspend your account for non-payment or misuse, with notice where practical; reads keep working during a suspension, writes stop. If you delete your organisation, you get a 30-day grace period to change your mind, then we permanently delete your data, including from the terminals.",
    },
    {
      heading: "7. Support access",
      body: "Our support team can open a read-only view of your account to help you, only when you ask or when we're investigating an issue. Every support session is time-limited and appears in your audit trail.",
    },
    {
      heading: "8. Availability",
      body: "We aim to keep the service available and fast. In this first version we do not offer a formal uptime guarantee (SLA).",
    },
    {
      heading: "9. Liability",
      body: 'We provide the service "as is" and "as available", to the extent the law allows. We are not liable for indirect, incidental, special or consequential loss, such as lost profits, lost data or business interruption, or for loss caused by your organisation\'s own configuration, its devices, its network or third-party services. Nothing in these terms limits liability that the law does not let us limit, such as liability for fraud. Our total liability for any claim is capped at the fees your organisation paid for the service in the 12 months before the claim arose.',
    },
    {
      heading: "10. Governing law",
      body: "These terms are governed by the laws of India. The courts at Chennai, Tamil Nadu have exclusive jurisdiction over any dispute arising from these terms or the service.",
    },
    {
      heading: "11. Changes to these terms",
      body: 'We may update these terms. For a material change, we\'ll tell you in the portal and ask you to accept again; continuing to use the service after a minor change means you accept it. Every version has a number (this one is below, under "Version").',
    },
  ],
};

/** Privacy notice, rendered at /legal/privacy. Counsel review is still recommended (see the source file). */
export const PRIVACY_DOCUMENT: LegalDocument = {
  title: "Privacy Notice",
  intro:
    "What Nexborg processes, why, and what the people an organisation enrols can ask for, under India's Digital Personal Data Protection Act 2023.",
  sections: [
    {
      heading: "1. Who we are",
      body: 'NexBorg Access is run by NexBorg Software Solutions Private Limited ("Nexborg"). We build and run the software that organisations use to manage face and card access on their premises. This notice explains what we process and why.',
    },
    {
      heading: "2. What we process",
      body: "Two groups of people, with different data:",
      bullets: [
        "For your organisation: your name, contact details, and how you use the service (billing, support requests, sign-in activity).",
        "For the people your organisation enrols (employees, contractors, visitors): name, mobile number (optional), card number, a face photo, the face template the terminal creates from it, and access events (time, door, method, a snapshot).",
      ],
    },
    {
      heading: "3. Why we process it",
      body: "Your organisation enrols people so they can use the terminals to get in and out, and so you have a log of who entered, when and where. We process this data to run that service for you, keep it secure, and support you when you ask.",
    },
    {
      heading: "4. Where it's stored",
      body: "Our servers and database are in India, with DigitalOcean in Bangalore. Photos and files are kept in Cloudflare R2 storage.",
    },
    {
      heading: "5. How long we keep it",
      body: 'Here is how long we keep each kind of record. "Set by your plan" means the number comes from the plan and add-ons your organisation has. "Set by your organisation" means your organisation chooses it, and the default is shown. A deleted record can stay in a backup until that backup expires.',
      table: {
        caption: "What we keep, and for how long",
        head: ["What", "How long we keep it", "Why"],
        rows: [
          [
            "Access events (time, door, method, result) and door status events",
            "Set by your plan (default 365 days)",
            "A log of who entered, when and where",
          ],
          [
            "Snapshots taken by the terminal at an entry",
            "Set by your plan (default 30 days)",
            "To check an entry later",
          ],
          [
            "Raw device records (what the terminal sent us, as received)",
            "Set by your plan (default 7 days)",
            "To investigate faults",
          ],
          [
            "Face photos",
            "While the person is enrolled, then 30 days after your organisation removes them. Deleted at once if they withdraw their consent",
            "To enrol their face on your terminals",
          ],
          [
            "Face templates (numbers made from a photo)",
            "On a terminal: until the person is removed from it. Our encrypted copy, kept only if your organisation uses Face Kiosk phones: until the person is removed, their photo is cleared or they withdraw their consent",
            "To recognise the person's face",
          ],
          [
            "Face Kiosk scans: the photo and the phone's location",
            "90 days. The photo goes within minutes when your organisation removes the person; the location stays for the 90 days. A scan that was never counted, such as a suspected fake, is deleted after 60 days. The scan record itself, without the photo, is kept for up to a year",
            "So a scan can be checked later",
          ],
          [
            "Staff punch selfies and locations (punches made from a phone)",
            "Set by your organisation (default 90 days, between 30 and 180). Kept longer only while that attendance month is still open, and never beyond 180 days",
            "To review a punch before the month is closed",
          ],
          [
            "Gym enquiries (the name and number of someone who has not joined)",
            "Set by your organisation (default 12 months, between 3 and 60) after the last contact. An enquiry that became a member stays while that member is enrolled",
            "To ring them back about membership",
          ],
          [
            "Documents attached to a profile, such as ID proofs",
            "Deleted 7 days after an employee or student leaves, and 30 days after a gym member, trainer, labourer or co-working member leaves. Removing the person starts the same clock",
            "To check who someone is while they are with the organisation",
          ],
          [
            "Text messages sent for your organisation, such as absence alerts",
            "The text and the mobile number are erased after 180 days. The date, status and cost stay",
            "To show what was sent and to account for message credits",
          ],
          ["Notification history in the app", "30 days", "To show recent alerts in the app inbox"],
          [
            "Sign-in codes and sessions",
            "A code lasts 5 minutes and is cleared within the hour. The note that a code was sent to a mobile number stays 31 days. A session record is deleted 14 days after it ends",
            "To sign you in and to limit repeated attempts",
          ],
          [
            "The audit trail (who changed what in your organisation, and when)",
            "Until your organisation is deleted",
            "So your organisation can see who did what",
          ],
          [
            "A removed person's record (name, mobile number, ID reference)",
            "Card numbers and fingerprints are deleted at once. The rest is hidden from lists but kept, because past events and reports refer to it, until your organisation is deleted",
            "So past events and reports still make sense",
          ],
          [
            "Our invoices to your organisation, and its payments to us",
            "While you have an account. After your organisation is deleted, until 31 December of the sixth year after the April–March financial year they belong to, as GST law requires. Only the invoice and payment details and your billing name, address and GSTIN are kept, never a payer's contact or card details",
            "Tax law requires us to keep our own sales records",
          ],
          [
            "Your organisation's own invoices, receipts and member ledger",
            "While your organisation has an account; they are deleted with it. Your data export includes them, so download it before you delete the organisation",
            "To bill your members and keep your accounts",
          ],
          [
            "Everything else, when an organisation is deleted",
            "Permanently deleted 30 days after the deletion is requested, a period to change your mind. A data export stays available for 7 days",
            "So an organisation can leave and take its data with it",
          ],
          ["Backups", "30 days", "To restore the service after a failure"],
        ],
      },
    },
    {
      heading: "6. Who else handles it (sub-processors)",
      body: "Three, and no one else:",
      bullets: [
        "DigitalOcean (Bangalore, India) — runs our servers and database.",
        "Cloudflare — object storage (photos, snapshots) and hosting for the portal.",
        "Airtel — sends SMS for sign-in codes.",
      ],
    },
    {
      heading: "7. How we protect it",
      body: "Face photos and card credentials are encrypted at rest. All traffic to and from the service uses TLS. Each organisation's data is isolated in the database so one organisation can never see another's. When our support team needs to look at your account, it's a read-only, time-limited session that is logged.",
    },
    {
      heading: "8. Your rights",
      body: "You (or, if an organisation enrolled you, your organisation on your behalf) can ask to see, correct or delete personal data, or withdraw consent where consent is the basis. If an organisation enrolled you, start with them — see the enrolment notice they showed you. For a concern about how we handle data, contact our grievance officer, Kumar, at support@nexborg.com.",
    },
    {
      heading: "9. Children",
      body: "This service isn't intended for people under 18 without a guardian's consent.",
    },
    {
      heading: "10. Changes",
      body: "We may update this notice. We'll post the new version here with a new version number and, for a material change, tell your organisation.",
    },
    {
      heading: "11. Contact",
      body: "NexBorg Software Solutions Private Limited. Email: support@nexborg.com.",
    },
  ],
};
